import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { fetchBatchStudentSkillStandards } from '@/store/slices';
import { SkillStandardResponse } from '@/models';
import useRosterInfo from './useRosterInfo';

interface SkillStandardsRequest {
  skillId: string;
  studentId: string;
  lessonPlanId: string;
}

interface UseSkillStandardsOptions {
  /** Whether to automatically fetch on mount */
  autoFetch?: boolean;
}

// Module-level cache with expiration and size management
interface CacheEntry {
  data: SkillStandardResponse[];
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
}

interface CacheConfig {
  maxSize: number;
  ttlMs: number; // Time to live in milliseconds
}

const CACHE_CONFIG: CacheConfig = {
  maxSize: 100, // Maximum number of entries
  ttlMs: 5 * 60 * 1000, // 5 minutes TTL
};

const globalCache: { [key: string]: CacheEntry } = {};
const pendingRequests: { [key: string]: Promise<any> } = {};

// Cache management utilities
const isCacheEntryValid = (entry: CacheEntry): boolean => {
  const now = Date.now();
  return (now - entry.timestamp) < CACHE_CONFIG.ttlMs;
};

const evictOldestEntries = (): void => {
  const entries = Object.entries(globalCache);
  if (entries.length <= CACHE_CONFIG.maxSize) return;

  // Sort by last accessed time (oldest first)
  entries.sort(([, a], [, b]) => a.lastAccessed - b.lastAccessed);
  
  // Remove oldest entries until we're under the limit
  const toRemove = entries.length - CACHE_CONFIG.maxSize;
  for (let i = 0; i < toRemove; i++) {
    delete globalCache[entries[i][0]];
  }
  
  console.log(`🧹 Evicted ${toRemove} old cache entries`);
};

const getCachedData = (key: string): SkillStandardResponse[] | null => {
  const entry = globalCache[key];
  if (!entry) return null;
  
  if (!isCacheEntryValid(entry)) {
    delete globalCache[key];
    console.log(`⏰ Cache entry expired for: ${key}`);
    return null;
  }
  
  // Update access tracking
  entry.lastAccessed = Date.now();
  entry.accessCount++;
  
  return entry.data;
};

const setCachedData = (key: string, data: SkillStandardResponse[]): void => {
  const now = Date.now();
  globalCache[key] = {
    data,
    timestamp: now,
    accessCount: 1,
    lastAccessed: now,
  };
  
  // Trigger eviction if needed
  evictOldestEntries();
};

// Context-based cache invalidation
let lastContextHash = '';

const getContextHash = (classroomId: string, userId: string): string => {
  // Derive context hash from stable identifiers
  return `${classroomId}-${userId}`;
};

const invalidateCacheIfContextChanged = (classroomId: string, userId: string): void => {
  const currentContextHash = getContextHash(classroomId, userId);
  if (lastContextHash && lastContextHash !== currentContextHash) {
    console.log('🔄 Context changed, clearing cache');
    clearCache();
  }
  lastContextHash = currentContextHash;
};

// Public cache management functions
const clearCache = (): void => {
  Object.keys(globalCache).forEach(key => delete globalCache[key]);
  Object.keys(pendingRequests).forEach(key => delete pendingRequests[key]);
  console.log('🧹 Cache cleared');
};

const getCacheStats = () => ({
  size: Object.keys(globalCache).length,
  maxSize: CACHE_CONFIG.maxSize,
  ttlMs: CACHE_CONFIG.ttlMs,
  entries: Object.entries(globalCache).map(([key, entry]) => ({
    key,
    age: Date.now() - entry.timestamp,
    accessCount: entry.accessCount,
    isValid: isCacheEntryValid(entry),
  })),
});

/**
 * Simple and efficient hook for skill standards
 * Uses module-level cache to prevent duplicate API calls
 */
export const useSkillStandards = (
  requests: SkillStandardsRequest[],
  options: UseSkillStandardsOptions = { autoFetch: true }
) => {
  const dispatch = useDispatch();
  const [skillStandards, setSkillStandards] = useState<{ [key: string]: SkillStandardResponse[] }>({});
  const [loading, setLoading] = useState(false);
  const { classroomId, userId } = useRosterInfo();
  // Stabilize requests with memoization to prevent unnecessary re-renders
  const stableRequests = useMemo(() => requests, [JSON.stringify(requests)]);
  const lastRequestsRef = useRef<string>('');

  const fetchStandards = useCallback(async () => {
    if (stableRequests.length === 0) return;

    // Check for context changes that should invalidate cache
    invalidateCacheIfContextChanged(classroomId, userId);

    // Check if requests have actually changed
    const currentRequestsHash = JSON.stringify(stableRequests);
    if (currentRequestsHash === lastRequestsRef.current) {
      console.log('⚡ Skipping fetch - requests unchanged');
      return;
    }
    lastRequestsRef.current = currentRequestsHash;

    console.log('🔍 useSkillStandards: fetchStandards called with', stableRequests.length, 'requests');

    // Check cache first and filter out requests we already have
    const requestsToFetch: SkillStandardsRequest[] = [];
    const cachedResults: { [key: string]: SkillStandardResponse[] } = {};

    const pendingPromises: Promise<any>[] = [];
    
    stableRequests.forEach(request => {
      const cacheKey = `${request.skillId}-${request.studentId}`;
      
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        // Use cached data
        console.log('✅ Using cached data for:', cacheKey);
        cachedResults[cacheKey] = cachedData;
      } else if (!pendingRequests[cacheKey]) {
        // Need to fetch this one
        console.log('📡 Need to fetch:', cacheKey);
        requestsToFetch.push(request);
      } else {
        // Wait for existing pending request
        console.log('⏳ Request already pending, waiting for:', cacheKey);
        pendingPromises.push(pendingRequests[cacheKey]);
      }
    });

    // Wait for any pending requests to complete
    if (pendingPromises.length > 0) {
      console.log('⏳ Waiting for', pendingPromises.length, 'pending requests to complete');
      try {
        await Promise.all(pendingPromises);
        
                 // After pending requests complete, check cache again for newly available data
         const newlyAvailableResults: { [key: string]: SkillStandardResponse[] } = {};
         stableRequests.forEach(request => {
           const cacheKey = `${request.skillId}-${request.studentId}`;
           const cachedData = getCachedData(cacheKey);
           if (cachedData && !cachedResults[cacheKey]) {
             console.log('✅ Now using data from completed request:', cacheKey);
             newlyAvailableResults[cacheKey] = cachedData;
           }
         });
        
        if (Object.keys(newlyAvailableResults).length > 0) {
          setSkillStandards(prev => ({ ...prev, ...newlyAvailableResults }));
        }
      } catch (error) {
        console.error('Error waiting for pending requests:', error);
      }
    }

    // Update with cached data first
    if (Object.keys(cachedResults).length > 0) {
      setSkillStandards(prev => ({ ...prev, ...cachedResults }));
    }

    // If no new requests needed, return early
    if (requestsToFetch.length === 0) {
      console.log('✅ No new requests needed, returning early');
      return;
    }

    setLoading(true);
    
    console.log('🚀 Making API call for', requestsToFetch.length, 'requests');
    
    try {
      // Store the actual dispatch promise to properly track in-flight requests
      const dispatchPromise = (dispatch as any)(fetchBatchStudentSkillStandards(requestsToFetch));
      
      // Mark requests as pending with the real promise to prevent duplicates
      requestsToFetch.forEach(request => {
        const cacheKey = `${request.skillId}-${request.studentId}`;
        if (!pendingRequests[cacheKey]) {
          pendingRequests[cacheKey] = dispatchPromise;
        }
      });

      const result: any = await dispatchPromise;
      
      if (result.payload?.responses) {
        const newStandards: { [key: string]: SkillStandardResponse[] } = {};
        
        result.payload.responses.forEach((response: any) => {
          const cacheKey = `${response.request.skillId}-${response.request.studentId}`;
          const standards = response.data || [];
          
          // Update both global cache and local state
          setCachedData(cacheKey, standards);
          newStandards[cacheKey] = standards;
        });
        
        setSkillStandards(prev => ({ ...prev, ...newStandards }));
      }
    } catch (error) {
      console.error('Failed to fetch skill standards:', error);
    } finally {
      setLoading(false);
      
      // Clear pending requests
      requestsToFetch.forEach(request => {
        const cacheKey = `${request.skillId}-${request.studentId}`;
        delete pendingRequests[cacheKey];
      });
    }
  }, [dispatch, stableRequests]);

  // Auto-fetch when component mounts or requests change
  useEffect(() => {
    console.log('🔄 useSkillStandards useEffect triggered. autoFetch:', options.autoFetch, 'requests.length:', stableRequests.length);
    if (options.autoFetch && stableRequests.length > 0) {
      fetchStandards();
    }
  }, [options.autoFetch, fetchStandards]);

  // Helper functions
  const getStandardsForSkill = useCallback((skillId: string, studentId: string): SkillStandardResponse[] => {
    const key = `${skillId}-${studentId}`;
    return skillStandards[key] || getCachedData(key) || [];
  }, [skillStandards]);

  const getStandardIdsForSkill = useCallback((skillId: string, studentId: string): string[] => {
    const standards = getStandardsForSkill(skillId, studentId);
    return standards.flatMap(s => s.standards?.map(std => std.standardId) || []);
  }, [getStandardsForSkill]);

  return {
    skillStandards,
    loading,
    fetchStandards,
    getStandardsForSkill,
    getStandardIdsForSkill,
    // Cache management utilities
    clearCache,
    getCacheStats,
  };
};

/**
 * Simplified hook for single skill standard requests
 */
export const useSkillStandard = (
  skillId: string,
  studentId: string,
  lessonPlanId: string,
  options: UseSkillStandardsOptions = { autoFetch: true }
) => {
  const requests = skillId && studentId && lessonPlanId 
    ? [{ skillId, studentId, lessonPlanId }] 
    : [];
    
  const result = useSkillStandards(requests, options);
  
  return {
    ...result,
    standards: result.getStandardsForSkill(skillId, studentId),
    standardIds: result.getStandardIdsForSkill(skillId, studentId),
  };
}; 